/**
 * The bell in the status bar (RP-34): the unread count at a glance, and a panel of the
 * newest unread notices that can be opened or dismissed in place. "Open inbox" goes to the
 * full-screen list at /dashboard/notifications.
 */

import { Link } from "@tanstack/react-router";
import { Bell } from "lucide-react";
import { useState } from "react";
import { NotificationItem } from "@/components/notifications/NotificationItem";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { useMarkAllNotificationsRead, useUnreadNotifications } from "@/hooks/use-notifications";
import { cn } from "@/lib/utils";

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const { data, isLoading, isError, refetch } = useUnreadNotifications();
  const dismissAll = useMarkAllNotificationsRead();
  const unread = data?.unreadCount ?? 0;
  const label =
    unread === 0 ? "Notifications" : `${unread} new notification${unread === 1 ? "" : "s"}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size={unread > 0 ? "xs" : "icon-xs"}
          aria-label={label}
          title={label}
          className={cn(
            "text-primary tabular-nums hover:bg-primary/10 hover:text-primary",
            unread > 0 &&
              "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground",
            open && unread === 0 && "bg-primary/10",
          )}
        >
          <Bell className="size-4" />
          {unread > 0 && <span>{unread > 99 ? "99+" : unread}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent side="top" align="end" className="w-96 gap-0 p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="font-display text-sm font-semibold">Notifications</span>
          {unread > 0 && (
            <Button
              size="xs"
              variant="ghost"
              disabled={dismissAll.isPending}
              onClick={() => dismissAll.mutate()}
            >
              Dismiss all
            </Button>
          )}
        </div>

        <div className="max-h-96 overflow-y-auto p-1.5">
          {isLoading && (
            <div className="space-y-2 p-1.5">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          )}
          {isError && (
            <div className="space-y-2 px-3 py-4 text-center text-muted-foreground">
              <p>Could not load notifications.</p>
              <Button size="sm" variant="outline" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          )}
          {data !== undefined && data.data.length === 0 && (
            <p className="px-3 py-6 text-center text-muted-foreground">You're all caught up.</p>
          )}
          {data?.data.map((n) => (
            <NotificationItem key={n.id} notification={n} onNavigate={() => setOpen(false)} />
          ))}
        </div>

        <div className="border-t border-border p-1.5">
          <Button asChild variant="ghost" size="sm" className="w-full">
            <Link to="/dashboard/notifications" onClick={() => setOpen(false)}>
              {data !== undefined && unread > data.data.length
                ? `Open inbox · ${unread - data.data.length} more`
                : "Open inbox"}
            </Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
